import { useCallback, useEffect, useMemo, useState } from "react";
import { rangeSince } from "@/utils";
import { toast } from "../../../components";
import { deriveLogs, DerivedLog, MetaDiff } from "./logDerivation";
import { DATE_OPTIONS, LOGS_PER_PAGE } from "./logFormatters";

interface Contributor {
  id?: string;
  name?: string;
  email?: string;
}

interface MediaLogsState {
  list: any[];
  count: number;
  isLoading: boolean;
  userContributorData?: Contributor[];
}

interface UseMediaLogsArgs {
  mediaLogsList: MediaLogsState;
  currentMedia: any;
  userList: { list?: any[] };
  getListMediaLogs: (params: any) => any;
  getUpdateMedia: (params: any) => Promise<any>;
}

interface Filters {
  page: number;
  rows: number;
}

interface FetchOpts {
  filterId?: string;
  system?: boolean;
  page?: number;
  rows?: number;
}

export const useMediaLogs = ({
  mediaLogsList,
  currentMedia,
  userList,
  getListMediaLogs,
  getUpdateMedia,
}: UseMediaLogsArgs) => {
  const mediaId: string | undefined = currentMedia?.id;

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all_time");
  const [systemLogs, setSystemLogs] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    page: 1,
    rows: LOGS_PER_PAGE,
  });
  const [revertingKey, setRevertingKey] = useState<string | null>(null);

  const fetchLogs = useCallback(
    (opts: FetchOpts = {}) => {
      if (!mediaId) return;
      const filterId = opts.filterId ?? dateFilter;
      const isSystem = opts.system ?? systemLogs;
      const mins = DATE_OPTIONS.find((o) => o.id === filterId)?.mins ?? 0;
      const range = rangeSince(mins, "ct");
      getListMediaLogs({
        key: mediaId,
        index: "mediameta",
        page: opts.page ?? filters.page,
        rows: opts.rows ?? filters.rows,
        ...(isSystem && { system: true }),
        ...(range && { range }),
      });
    },
    [
      mediaId,
      dateFilter,
      systemLogs,
      filters.page,
      filters.rows,
      getListMediaLogs,
    ]
  );

  useEffect(() => {
    if (mediaId) fetchLogs({ filterId: "all_time", page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  const changeDateFilter = useCallback(
    (id: string) => {
      setDateFilter(id);
      setFilters((f) => ({ ...f, page: 1 }));
      fetchLogs({ filterId: id, page: 1 });
    },
    [fetchLogs]
  );

  const toggleSystemLogs = useCallback(() => {
    const next = !systemLogs;
    setSystemLogs(next);
    setFilters((f) => ({ ...f, page: 1 }));
    fetchLogs({ system: next, page: 1 });
  }, [systemLogs, fetchLogs]);

  const handleSetFilters = useCallback(
    (next: Record<string, any>) => {
      const page = Number(next.page ?? filters.page) || 1;
      const rows = Number(next.rows ?? filters.rows) || LOGS_PER_PAGE;
      setFilters({ page, rows });
      fetchLogs({ page, rows });
    },
    [filters.page, filters.rows, fetchLogs]
  );

  const revertPayloadFor = (diff: MetaDiff): Record<string, any> =>
    diff.key.startsWith("images")
      ? { images: diff.beforeRaw }
      : { [diff.key]: diff.beforeRaw };

  const runRevert = useCallback(
    async (
      key: string,
      payload: Record<string, any>,
      successMsg: string,
      failMsg: string
    ) => {
      if (!mediaId) return;
      setRevertingKey(key);
      try {
        const res = await getUpdateMedia({
          id: mediaId,
          title: currentMedia?.title,
          ...payload,
        });
        if (res?.status === 200) {
          toast.success(successMsg);
          fetchLogs();
        } else {
          toast.fail(failMsg);
        }
      } catch {
        toast.fail(failMsg);
      } finally {
        setRevertingKey(null);
      }
    },
    [mediaId, currentMedia?.title, getUpdateMedia, fetchLogs]
  );

  const revertField = useCallback(
    (diff: MetaDiff) => {
      if (!diff.hasBefore) return;
      runRevert(
        `${mediaId}-${diff.key}`,
        revertPayloadFor(diff),
        `${diff.field} reverted to previous value`,
        `Could not revert ${diff.field}`
      );
    },
    [mediaId, runRevert]
  );

  const revertAll = useCallback(
    (logId: string, diffs: MetaDiff[]) => {
      const revertable = diffs.filter((d) => d.hasBefore);
      if (revertable.length === 0) return;
      const payload = revertable.reduce(
        (acc, d) => Object.assign(acc, revertPayloadFor(d)),
        {} as Record<string, any>
      );
      runRevert(
        `${mediaId}-all-${logId}`,
        payload,
        `${revertable.length} field${
          revertable.length > 1 ? "s" : ""
        } reverted to previous values`,
        "Could not revert changes"
      );
    },
    [mediaId, runRevert]
  );

  const contributorById = useMemo(() => {
    const map: Record<string, Contributor> = {};
    const add = (u?: Contributor) => {
      if (u?.id && (u.name || u.email) && !map[u.id]) map[u.id] = u;
    };
    (mediaLogsList.userContributorData ?? []).forEach(add);
    add(currentMedia?.ctbydata);
    add(currentMedia?.mtbydata);
    (userList?.list ?? []).forEach(add);
    return map;
  }, [mediaLogsList.userContributorData, currentMedia, userList]);

  // Author chip: just the name (falls back to email, then raw id).
  const resolveUserName = useCallback(
    (userId?: string): string | null => {
      if (!userId) return null;
      const u = contributorById[userId];
      return u?.name || u?.email || userId;
    },
    [contributorById]
  );

  // Contributor field values (script_writer, producer, …): "name (email)".
  // Unknown strings (tags, keywords) pass through untouched.
  const resolveNameForValue = useCallback(
    (id: string): string => {
      const u = contributorById[id];
      if (!u) return id;
      const name = u.name || u.email || id;
      return u.email && u.name ? `${name} (${u.email})` : name;
    },
    [contributorById]
  );

  const logs: DerivedLog[] = useMemo(
    () =>
      deriveLogs(mediaLogsList.list, mediaId, systemLogs, resolveNameForValue),
    [mediaLogsList.list, mediaId, systemLogs, resolveNameForValue]
  );

  const filteredLogs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter(
      (log) =>
        log.title.toLowerCase().includes(q) ||
        log.description.toLowerCase().includes(q) ||
        (resolveUserName(log.userId) ?? "").toLowerCase().includes(q)
    );
  }, [logs, searchQuery, resolveUserName]);

  return {
    mediaId,
    searchQuery,
    setSearchQuery,
    dateFilter,
    systemLogs,
    filters,
    revertingKey,
    isLoading: mediaLogsList.isLoading,
    totalCount: mediaLogsList.count,
    filteredLogs,
    fetchLogs,
    changeDateFilter,
    toggleSystemLogs,
    handleSetFilters,
    revertField,
    revertAll,
    resolveUserName,
  };
};
